if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      createAjpoGroupId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
          return `ajp_${window.crypto.randomUUID()}`;
        }

        return `ajp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      }

      getSelectedAjpoPaidOptions() {
        return [...document.querySelectorAll('[data-ajp-paid-option]:checked')].filter(
          (option) => option.form === this.form && !option.disabled
        );
      }

      getAjpoProperties(formData) {
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const match = name.match(/^properties\[(.*)\]$/);
          if (!match || value === '') continue;

          properties[match[1]] = value;
        }

        return properties;
      }

      buildAjpoRequest(formData, paidOptions, sections) {
        const variantId = Number(formData.get('id'));
        const quantity = Math.max(1, Number(formData.get('quantity') || 1));
        const groupId = this.createAjpoGroupId();
        const properties = this.getAjpoProperties(formData);

        // Private property used to associate this parent line with its add-ons.
        properties._ajp_group = groupId;

        const mainItem = {
          id: variantId,
          quantity,
          properties,
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) mainItem.selling_plan = sellingPlan;

        const items = [mainItem];

        paidOptions.forEach((option) => {
          const addonVariantId = Number(option.dataset.ajpAddonVariant);
          if (!addonVariantId) return;

          const addonQuantity =
            option.dataset.ajpAddonQuantity === 'match' ? quantity : 1;

          items.push({
            id: addonVariantId,
            quantity: addonQuantity,
            properties: {
              _ajp_addon: option.dataset.ajpAddonKey || 'addon',
              _ajp_addon_label: option.dataset.ajpAddonLabel || 'Add-on',
              _ajp_parent_group: groupId,
              _ajp_parent_variant: String(variantId),
            },
          });
        });

        const body = { items };

        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }

        return body;
      }

      async recoverMissingCartSections(response, sections) {
        if (!this.cart || !sections.length) return response;

        const missing = sections.filter((id) => !response.sections?.[id]);

        if (!missing.length) return response;

        console.warn(
          '[AJP Cart] Missing bundled sections; fetching separately:',
          missing
        );

        const url = new URL(window.location.pathname, window.location.origin);
        url.searchParams.set('sections', missing.join(','));

        const result = await fetch(url.toString(), {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!result.ok) {
          throw new Error(`Cart section recovery failed: HTTP ${result.status}`);
        }

        const recovered = await result.json();

        response.sections = {
          ...(response.sections || {}),
          ...recovered,
        };

        const stillMissing = sections.filter((id) => !response.sections[id]);

        if (stillMissing.length) {
          throw new Error(
            `Cart sections still missing after recovery: ${stillMissing.join(', ')}`
          );
        }

        return response;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // Respect native HTML validation before doing any Ajax work.
        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const paidOptions = this.getSelectedAjpoPaidOptions();
        const hasAjpoPaidOptions = paidOptions.length > 0;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        let sections = [];

        if (this.cart) {
          sections = this.cart.getSectionsToRender().map((section) => section.id);
          this.cart.setActiveElement(document.activeElement);
        }

        if (hasAjpoPaidOptions) {
          // AJPO paid options require Shopify's multi-item JSON cart API.
          // Do NOT delete Content-Type here: fetchConfig's JSON header is required.
          config.body = JSON.stringify(this.buildAjpoRequest(formData, paidOptions, sections));
        } else {
          // Existing AJP/Dawn behaviour remains untouched for ordinary products.
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

            this.error = false;

            // GJ20261030: Shopify bug identified - Recover sections if Shopify's bundled rendering returned null. (https://community.shopify.dev/t/sections-api-is-sometimes-returning-null/39219)
            response = await this.recoverMissingCartSections(response, sections);

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(
              'Something went wrong while adding this product to your basket. Please try again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );

  async function updateCartIconBubble() {
    try {
      const res = await fetch(`${window.location.pathname}?sections=cart-icon-bubble`);
      if (!res.ok) return;
      const json = await res.json();
      const html = json['cart-icon-bubble'];
      if (!html) return;

      const tmp = document.createElement('div');
      tmp.innerHTML = html;

      // Prefer replacing just the anchor if present; otherwise replace the whole section
      const fresh = tmp.querySelector('#cart-icon-bubble') || tmp.querySelector('.shopify-section');
      const current =
        document.querySelector('#cart-icon-bubble') ||
        document.getElementById('shopify-section-cart-icon-bubble');

      if (fresh && current) current.replaceWith(fresh);
    } catch (e) {
      // no-op
    }
  }
}
